import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  styled,
  Typography,
  Chip,
  Tooltip,
  Divider,
  InputAdornment,
  IconButton,
  Card,
} from '@mui/material';
import * as Icons from '@mui/icons-material';
import { Property, PropertyStatus } from '../types/property';
import {
  calculatePerfectRentForHoldScore,
  calculatePerfectARVForFlipScore
} from '../utils/scoreCalculator';
import { getStatusColor } from '../utils/statusColors';
import { usePropertyForm } from '../hooks';

// Styled MenuItem for status dropdown
const StyledMenuItem = styled(MenuItem)<{ statuscolor: string }>(({ statuscolor }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  '&::before': {
    content: '""',
    display: 'block',
    width: '16px',
    height: '16px',
    borderRadius: '50%',
    backgroundColor: statuscolor,
  },
}));

/** Quiet section label with a hairline rule, matching the report tables */
const SectionHeader: React.FC<{ label: string; action?: React.ReactNode }> = ({ label, action }) => (
  <Box sx={{ mt: 3, mb: 2 }}>
    <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ letterSpacing: 1, fontWeight: 600 }}
      >
        {label}
      </Typography>
      {action}
    </Box>
    <Divider />
  </Box>
);

interface PropertyDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (property: Omit<Property, 'id'>) => Promise<void>;
  property?: Property | null;
  isEditing: boolean;
}

const PropertyDialog: React.FC<PropertyDialogProps> = ({
  open,
  onClose,
  onSave,
  property,
  isEditing
}) => {
  // Use the property form hook for state management
  const {
    formData,
    errors,
    updateField,
    updateExpense,
    updateCapitalCost,
    addUnit: hookAddUnit,
    updateUnit: hookUpdateUnit,
    updateUnitStatus: hookUpdateUnitStatus,
    removeUnit: hookRemoveUnit,
    validate,
    reset,
    getSubmitData
  } = usePropertyForm(isEditing ? property || undefined : undefined);

  const [unitStatusChangeDates, setUnitStatusChangeDates] = useState<{ [key: number]: string }>({});


  // Validation helpers for unit numbers
  const getUnitNumberError = (unitNumber: string, currentIndex: number): string => {
    // Check if empty
    if (!unitNumber || unitNumber.trim() === '') {
      return 'Unit number is required';
    }

    // Check format (alphanumeric, hyphen, underscore only)
    const validFormat = /^[a-zA-Z0-9_-]+$/;
    if (!validFormat.test(unitNumber.trim())) {
      return 'Unit number can only contain letters, numbers, hyphens, and underscores';
    }

    // Check for duplicates (case-insensitive)
    const isDuplicate = formData.propertyUnits.some((u, idx) =>
      idx !== currentIndex && u.unitNumber.toLowerCase() === unitNumber.trim().toLowerCase()
    );
    if (isDuplicate) {
      return 'Unit number must be unique within this property';
    }

    return '';
  };

  const addUnit = () => {
    const newUnitIndex = formData.propertyUnits.length;

    // Use the hook to add the unit
    hookAddUnit();

    // Initialize status change date for the new unit (empty)
    setUnitStatusChangeDates(prev => ({
      ...prev,
      [newUnitIndex]: ''
    }));
  };

  const updateUnit = (index: number, field: string, value: string | number) => {
    hookUpdateUnit(index, field, value);
  };

  const updateUnitStatus = (index: number, newStatus: string, statusChangeDate: string) => {
    hookUpdateUnitStatus(index, newStatus, statusChangeDate);
  };

  const removeUnit = (index: number) => {
    hookRemoveUnit(index);

    // Clean up status change date state
    setUnitStatusChangeDates(prev => {
      const newDates = { ...prev };
      delete newDates[index];
      // Shift indices for units after the removed one
      const shiftedDates: { [key: number]: string } = {};
      Object.keys(newDates).forEach(key => {
        const numKey = parseInt(key);
        if (numKey > index) {
          shiftedDates[numKey - 1] = newDates[numKey];
        } else {
          shiftedDates[numKey] = newDates[numKey];
        }
      });
      return shiftedDates;
    });
  };

  // Status color function is now imported from utils/statusColors.ts

  const handleCurrencyInput = (value: string) => {
    // Remove any non-digit characters
    const numericValue = value.replace(/[^0-9]/g, '');
    return numericValue ? parseInt(numericValue) : 0;
  };

  const handleCurrencyInputNullable = (value: string): number | null => {
    if (value === '') return null;
    // Remove any non-digit characters
    const numericValue = value.replace(/[^0-9]/g, '');
    return numericValue ? parseInt(numericValue) : null;
  };

  const formatInputCurrency = (value: number) => {
    if (value === 0) return '';
    return value.toLocaleString('en-US');
  };

  const formatInputCurrencyNullable = (value: number | null) => {
    if (value === null || value === 0) return '';
    return value.toLocaleString('en-US');
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Calculate perfect values for recommendations
  const getPerfectRentRecommendation = () => {
    if (formData.offerPrice > 0 || formData.rehabCosts > 0) {
      const perfectRent = calculatePerfectRentForHoldScore(
        formData.offerPrice,
        formData.rehabCosts,
        formData.arv || 0,
        formData.units || 1
      );
      return perfectRent;
    }
    return null;
  };

  const getPerfectARVRecommendation = () => {
    if (formData.offerPrice > 0 || formData.rehabCosts > 0) {
      const perfectARV = calculatePerfectARVForFlipScore(
        formData.offerPrice,
        formData.rehabCosts
      );
      return perfectARV;
    }
    return null;
  };

  const parseZillowLink = (url: string) => {
    try {
      // Extract address from URL
      const addressMatch = url.match(/\/homedetails\/([^/]+)/);
      if (addressMatch) {
        const address = addressMatch[1]
          .split('-')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        
        // Extract price from URL - try multiple patterns
        let price = 0;
        
        // Try to find price in the URL path
        const pathPriceMatch = url.match(/\$(\d{3}(?:,\d{3})*(?:\.\d{2})?)/);
        if (pathPriceMatch) {
          price = handleCurrencyInput(pathPriceMatch[1]);
        }
        
        // If no price in path, try to find it in the title/description part
        if (!price) {
          const titlePriceMatch = url.match(/title=([^&]+)/);
          if (titlePriceMatch) {
            const title = decodeURIComponent(titlePriceMatch[1]);
            const priceMatch = title.match(/\$(\d{3}(?:,\d{3})*(?:\.\d{2})?)/);
            if (priceMatch) {
              price = handleCurrencyInput(priceMatch[1]);
            }
          }
        }

        return { address, price };
      }
      return null;
    } catch (error) {
      console.error('Error parsing Zillow link:', error);
      return null;
    }
  };

  const handleZillowLinkChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const url = e.target.value;
    updateField('zillowLink', url);

    // Only parse if we're not editing and the URL is a valid Zillow link
    if (!isEditing && url.includes('zillow.com')) {
      const parsedData = parseZillowLink(url);
      if (parsedData) {
        updateField('address', parsedData.address);
      }
    }
  };

  // Unit numbers are validated inline; the hook covers the rest of the form
  const hasUnitNumberErrors = formData.propertyUnits.some(
    (unit, index) => getUnitNumberError(unit.unitNumber, index) !== ''
  );

  const handleSave = async () => {
    if (!validate() || hasUnitNumberErrors) return;

    try {
      // Get the form data and calculate actual rent before saving
      const submitData = getSubmitData();
      const totalRent = submitData.propertyUnits.reduce((sum, unit) => sum + unit.rent, 0);
      const propertyToSave = {
        ...submitData,
        actualRent: totalRent
      };
      await onSave(propertyToSave);
      handleClose();
    } catch (error) {
      console.error('Error saving property:', error);
    }
  };

  const handleClose = () => {
    reset();
    setUnitStatusChangeDates({});
    onClose();
  };

  // Update form when property prop changes (for editing)
  useEffect(() => {
    if (property && isEditing) {
      reset(property);

      // Initialize status change dates for existing units
      const initialStatusChangeDates: { [key: number]: string } = {};
      property.propertyUnits.forEach((unit, index) => {
        if (unit.statusHistory && unit.statusHistory.length > 0) {
          // Use the date from the last status change
          const lastStatusChange = unit.statusHistory[unit.statusHistory.length - 1];
          initialStatusChangeDates[index] = lastStatusChange.dateStart.split('T')[0];
        }
        // No default date - leave empty if no status history
      });
      setUnitStatusChangeDates(initialStatusChangeDates);
    }
  }, [property, isEditing, reset]);

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth 
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ fontSize: '1.25rem', fontWeight: 500, pb: 1 }}>
        {isEditing ? 'Edit Property' : 'Add New Property'}
      </DialogTitle>
      <DialogContent dividers sx={{ maxHeight: '80vh', overflow: 'auto' }}>
        <Box sx={{ p: 1 }}>
          {/* Basic Information */}
          <Box sx={{ mb: 3 }}>
            <SectionHeader label="Basic Information" />
            <TextField
              fullWidth
              label="Zillow Link"
              value={formData.zillowLink}
              onChange={handleZillowLinkChange}
              margin="normal"
              placeholder="Paste Zillow link to auto-fill address and price"
              variant="outlined"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Address"
              value={formData.address}
              onChange={(e) => updateField('address', e.target.value)}
              margin="normal"
              required
              error={!!errors.address}
              helperText={errors.address}
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                onChange={(e) => updateField('status', e.target.value as PropertyStatus)}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box 
                      sx={{ 
                        width: 16, 
                        height: 16, 
                        borderRadius: '50%', 
                        backgroundColor: getStatusColor(selected as PropertyStatus),
                      }} 
                    />
                    {selected}
                  </Box>
                )}
              >
                <StyledMenuItem value="Opportunity" statuscolor={getStatusColor('Opportunity')}>
                  Opportunity
                </StyledMenuItem>
                <StyledMenuItem value="Soft Offer" statuscolor={getStatusColor('Soft Offer')}>
                  Soft Offer
                </StyledMenuItem>
                <StyledMenuItem value="Hard Offer" statuscolor={getStatusColor('Hard Offer')}>
                  Hard Offer
                </StyledMenuItem>
                <StyledMenuItem value="Rehab" statuscolor={getStatusColor('Rehab')}>
                  Rehab
                </StyledMenuItem>
                <StyledMenuItem value="Operational" statuscolor={getStatusColor('Operational')}>
                  Operational
                </StyledMenuItem>
                <StyledMenuItem value="Needs Tenant" statuscolor={getStatusColor('Needs Tenant')}>
                  Needs Tenant
                </StyledMenuItem>
                <StyledMenuItem value="Selling" statuscolor={getStatusColor('Selling')}>
                  Selling
                </StyledMenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Notes"
              multiline
              rows={3}
              value={formData.notes}
              onChange={(e) => updateField('notes', e.target.value)}
              margin="normal"
            />
          </Box>

          {/* Investment Details Section */}
          <Box sx={{ mb: 2 }}>
            <SectionHeader label="Investment Details" />
            <Box>
              <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2, 1fr)' }} gap={2}>
                <TextField
                  fullWidth
                  label="Listing Price"
                  value={formatInputCurrency(formData.listingPrice)}
                  onChange={(e) => updateField('listingPrice', handleCurrencyInput(e.target.value))}
                  margin="normal"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
                <TextField
                  fullWidth
                  label="Offer Price"
                  value={formatInputCurrency(formData.offerPrice)}
                  onChange={(e) => updateField('offerPrice', handleCurrencyInput(e.target.value))}
                  margin="normal"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
                <TextField
                  fullWidth
                  label="Rehab Costs"
                  value={formatInputCurrency(formData.rehabCosts)}
                  onChange={(e) => updateField('rehabCosts', handleCurrencyInput(e.target.value))}
                  margin="normal"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
                <TextField
                  fullWidth
                  label="Square Footage"
                  value={formData.squareFootage !== null ? formData.squareFootage : ''}
                  onChange={(e) => {
                    const value = e.target.value.trim() === '' ? null : parseInt(e.target.value, 10);
                    updateField('squareFootage', value);
                  }}
                  margin="normal"
                  type="number"
                />
                <TextField
                  fullWidth
                  label="Units"
                  value={formData.units !== null ? formData.units : ''}
                  onChange={(e) => {
                    const value = e.target.value.trim() === '' ? null : parseInt(e.target.value, 10);
                    updateField('units', value);
                  }}
                  margin="normal"
                  type="number"
                />
              </Box>
              
              <Tooltip
                title={
                  getPerfectRentRecommendation() ? (
                    <Box sx={{ p: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                        Perfect Hold Score (10/10): {formatCurrency(getPerfectRentRecommendation()!)}/month
                      </Typography>
                      <Typography variant="body2">
                        This rent amount will achieve:
                      </Typography>
                      <Typography variant="body2" component="ul" sx={{ mt: 0.5, pl: 2 }}>
                        <li>$200+ cashflow per unit (8/8 points)</li>
                        <li>1%+ rent ratio (2/2 points)</li>
                      </Typography>
                    </Box>
                  ) : (
                    "Enter offer price and rehab costs to see perfect rent recommendation"
                  )
                }
                arrow
                placement="top-start"
              >
                <TextField
                  fullWidth
                  label="Potential Monthly Rent"
                  value={formatInputCurrency(formData.potentialRent)}
                  onChange={(e) => updateField('potentialRent', handleCurrencyInput(e.target.value))}
                  margin="normal"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  helperText={
                    getPerfectRentRecommendation() ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip 
                          label="Perfect Hold Score (10/10)" 
                          size="small" 
                          color="success" 
                          variant="outlined"
                          sx={{ fontSize: '0.7rem', height: '20px' }}
                        />
                        <Typography variant="caption" color="success.main" fontWeight="medium">
                          {formatCurrency(getPerfectRentRecommendation()!)}/month
                        </Typography>
                      </Box>
                    ) : (
                      "Enter offer price and rehab costs to see perfect rent recommendation"
                    )
                  }
                />
              </Tooltip>
              
              <Tooltip
                title={
                  getPerfectARVRecommendation() ? (
                    <Box sx={{ p: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                        Perfect Flip Score (10/10): {formatCurrency(getPerfectARVRecommendation()!)}
                      </Typography>
                      <Typography variant="body2">
                        This ARV amount will achieve:
                      </Typography>
                      <Typography variant="body2" component="ul" sx={{ mt: 0.5, pl: 2 }}>
                        <li>≤65% ARV ratio (10/10 pts)</li>
                      </Typography>
                    </Box>
                  ) : (
                    "Enter offer price and rehab costs to see perfect ARV recommendation"
                  )
                }
                arrow
                placement="top-start"
              >
                <TextField
                  fullWidth
                  label="ARV"
                  value={formatInputCurrency(formData.arv)}
                  onChange={(e) => updateField('arv', handleCurrencyInput(e.target.value))}
                  margin="normal"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  helperText={
                    getPerfectARVRecommendation() ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip 
                          label="Perfect Flip Score (10/10)" 
                          size="small" 
                          color="warning" 
                          variant="outlined"
                          sx={{ fontSize: '0.7rem', height: '20px' }}
                        />
                        <Typography variant="caption" color="warning.main" fontWeight="medium">
                          {formatCurrency(getPerfectARVRecommendation()!)}
                        </Typography>
                      </Box>
                    ) : (
                      "Enter offer price and rehab costs to see perfect ARV recommendation"
                    )
                  }
                />
              </Tooltip>

              <TextField
                fullWidth
                label="Property Lead ID"
                value={formData.propertyLeadId || ''}
                onChange={(e) => updateField('propertyLeadId', e.target.value || null)}
                margin="normal"
                placeholder="Optional - link to original lead"
              />
            </Box>
          </Box>

          {/* Operational Details Section */}
          <Box sx={{ mb: 2 }}>
            <Box>
              {/* Monthly Expenses */}
              <Box sx={{ mb: 3 }}>
                <SectionHeader label="Monthly Expenses" />
                <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2, 1fr)' }} gap={2}>
                  <TextField
                    fullWidth
                    label="Mortgage"
                    value={formatInputCurrency(formData.monthlyExpenses?.mortgage || 0)}
                    onChange={(e) => updateExpense('mortgage', handleCurrencyInput(e.target.value))}
                    margin="normal"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Taxes"
                    value={formatInputCurrency(formData.monthlyExpenses?.taxes || 0)}
                    onChange={(e) => updateExpense('taxes', handleCurrencyInput(e.target.value))}
                    margin="normal"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Insurance"
                    value={formatInputCurrency(formData.monthlyExpenses?.insurance || 0)}
                    onChange={(e) => updateExpense('insurance', handleCurrencyInput(e.target.value))}
                    margin="normal"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Property Management"
                    value={formatInputCurrency(formData.monthlyExpenses?.propertyManagement || 0)}
                    onChange={(e) => updateExpense('propertyManagement', handleCurrencyInput(e.target.value))}
                    margin="normal"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Utilities"
                    value={formatInputCurrency(formData.monthlyExpenses?.utilities || 0)}
                    onChange={(e) => updateExpense('utilities', handleCurrencyInput(e.target.value))}
                    margin="normal"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Vacancy"
                    value={formatInputCurrency(formData.monthlyExpenses?.vacancy || 0)}
                    onChange={(e) => updateExpense('vacancy', handleCurrencyInput(e.target.value))}
                    margin="normal"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                  />
                  <TextField
                    fullWidth
                    label="CapEx"
                    value={formatInputCurrency(formData.monthlyExpenses?.capEx || 0)}
                    onChange={(e) => updateExpense('capEx', handleCurrencyInput(e.target.value))}
                    margin="normal"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Other"
                    value={formatInputCurrency(formData.monthlyExpenses?.other || 0)}
                    onChange={(e) => updateExpense('other', handleCurrencyInput(e.target.value))}
                    margin="normal"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                  />
                </Box>
                <Box sx={{ mt: 2, p: 2, backgroundColor: 'action.hover', borderRadius: 1 }}>
                  <Typography variant="subtitle1" fontWeight={600} color="error.main">
                    Total Monthly Expenses: {formatCurrency(formData.monthlyExpenses?.total || 0)}
                  </Typography>
                </Box>
              </Box>

              {/* Units */}
              <Box>
                <SectionHeader
                  label={`Units (${formData.propertyUnits.length})`}
                  action={
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={addUnit}
                      startIcon={<Icons.Add />}
                    >
                      Add Unit
                    </Button>
                  }
                />

                {formData.propertyUnits.map((unit, index) => (
                  <Card key={unit.id} variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Unit {unit.unitNumber}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => removeUnit(index)}
                        color="error"
                      >
                        <Icons.Delete />
                      </IconButton>
                    </Box>

                    <Box mb={2}>
                      <TextField
                        fullWidth
                        label="Unit Number"
                        value={unit.unitNumber}
                        onChange={(e) => updateUnit(index, 'unitNumber', e.target.value)}
                        required
                        error={!!getUnitNumberError(unit.unitNumber, index)}
                        helperText={getUnitNumberError(unit.unitNumber, index)}
                      />
                    </Box>

                    <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2, 1fr)' }} gap={2}>
                      <FormControl fullWidth>
                        <InputLabel>Status</InputLabel>
                        <Select
                          value={unit.status}
                          onChange={(e) => {
                            const newStatus = e.target.value;
                            const statusChangeDate = unitStatusChangeDates[index];
                            if (statusChangeDate) {
                              updateUnitStatus(index, newStatus, statusChangeDate);
                            }
                          }}
                          label="Status"
                        >
                          <MenuItem value="Vacant">Vacant</MenuItem>
                          <MenuItem value="Behind On Rent">Behind On Rent</MenuItem>
                          <MenuItem value="Operational">Operational</MenuItem>
                        </Select>
                      </FormControl>
                      
                      <TextField
                        fullWidth
                        label="Monthly Rent"
                        value={formatInputCurrency(unit.rent)}
                        onChange={(e) => updateUnit(index, 'rent', handleCurrencyInput(e.target.value))}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                      />
                    </Box>

                    <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2, 1fr)' }} gap={2} sx={{ mt: 2 }}>
                      <TextField
                        fullWidth
                        type="date"
                        label="Lease Date"
                        value={unit.leaseDate ? new Date(unit.leaseDate).toISOString().split('T')[0] : ''}
                        onChange={(e) => updateUnit(index, 'leaseDate', e.target.value ? new Date(e.target.value + 'T12:00:00.000Z').toISOString() : '')}
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                      <TextField
                        fullWidth
                        label="Status Change Date"
                        type="date"
                        value={unitStatusChangeDates[index] || ''}
                        onChange={(e) => {
                          const newDate = e.target.value;
                          
                          // Only proceed if we have a valid date
                          if (!newDate) {
                            return;
                          }

                          setUnitStatusChangeDates(prev => ({
                            ...prev,
                            [index]: newDate
                          }));

                          // Also update the status history with the new date
                          const currentStatus = unit.status;

                          try {
                            const isoDate = new Date(newDate + 'T00:00:00.000Z').toISOString();

                            const updatedUnits = formData.propertyUnits.map((u, i) => {
                              if (i === index) {
                                const newStatusHistory = [...u.statusHistory];

                                // Update the last status entry with the new date, or add a new one if none exists
                                if (newStatusHistory.length > 0) {
                                  newStatusHistory[newStatusHistory.length - 1] = {
                                    status: currentStatus,
                                    dateStart: isoDate
                                  };
                                } else {
                                  newStatusHistory.push({
                                    status: currentStatus,
                                    dateStart: isoDate
                                  });
                                }

                                return {
                                  ...u,
                                  statusHistory: newStatusHistory
                                };
                              }
                              return u;
                            });
                            updateField('propertyUnits', updatedUnits);
                          } catch (error) {
                            console.error('Invalid date format:', newDate, error);
                          }
                        }}
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                    </Box>
                    
                    <TextField
                      fullWidth
                      label="Notes"
                      value={unit.notes}
                      onChange={(e) => updateUnit(index, 'notes', e.target.value)}
                      margin="normal"
                      multiline
                      rows={2}
                    />
                  </Card>
                ))}
                
                {formData.propertyUnits.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                    No units added yet. Click "Add Unit" to get started.
                  </Typography>
                )}

                <Box sx={{ mt: 2, p: 2, backgroundColor: 'action.hover', borderRadius: 1 }}>
                  <Typography variant="subtitle1" fontWeight={600} color="success.main">
                    Total Actual Rent: {formatCurrency(formData.actualRent)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Asset Details Section */}
          <Box sx={{ mb: 2 }}>
            <Box>
              <Box>
                <SectionHeader label="Property Value" />
                <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2, 1fr)' }} gap={2}>
                  <TextField
                    fullWidth
                    label="Current House Value"
                    value={formatInputCurrency(formData.currentHouseValue)}
                    onChange={(e) => updateField('currentHouseValue', handleCurrencyInput(e.target.value))}
                    margin="normal"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Current Loan Value"
                    value={formatInputCurrencyNullable(formData.currentLoanValue)}
                    onChange={(e) => updateField('currentLoanValue', handleCurrencyInputNullable(e.target.value))}
                    margin="normal"
                    placeholder="Optional"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                  />
                </Box>
              </Box>
              
              <Box sx={{ mt: 3 }}>
                <SectionHeader label="Capital Costs" />
                <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2, 1fr)' }} gap={2}>
                  <TextField
                    fullWidth
                    label="Closing Costs"
                    value={formatInputCurrency(formData.capitalCosts?.closingCosts || 0)}
                    onChange={(e) => updateCapitalCost('closingCosts', handleCurrencyInput(e.target.value))}
                    margin="normal"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Upfront Repairs"
                    value={formatInputCurrency(formData.capitalCosts?.upfrontRepairs || 0)}
                    onChange={(e) => updateCapitalCost('upfrontRepairs', handleCurrencyInput(e.target.value))}
                    margin="normal"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Down Payment"
                    value={formatInputCurrency(formData.capitalCosts?.downPayment || 0)}
                    onChange={(e) => updateCapitalCost('downPayment', handleCurrencyInput(e.target.value))}
                    margin="normal"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Other"
                    value={formatInputCurrency(formData.capitalCosts?.other || 0)}
                    onChange={(e) => updateCapitalCost('other', handleCurrencyInput(e.target.value))}
                    margin="normal"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                  />
                </Box>
                <Box sx={{ mt: 2, p: 2, backgroundColor: 'action.hover', borderRadius: 1 }}>
                  <Typography variant="subtitle1" fontWeight={600} color="primary.main">
                    Total Capital Costs: {formatCurrency(formData.capitalCosts?.total || 0)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} color="inherit" sx={{ borderRadius: 2 }}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={hasUnitNumberErrors}
          sx={{ borderRadius: 2 }}
        >
          {isEditing ? 'Update' : 'Add'} Property
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PropertyDialog; 